//
// Copyright 2025 DXOS.org
//

import type * as Effect from 'effect/Effect';

import { createAnnotationHelper } from '@dxos/echo/internal';
import { HueAnnotationId, StepAnnotationId } from '@dxos/ui-types';

/** One selectable option produced by an {@link OptionsLookup}. */
export type OptionsLookupEntry = { value: string; label?: string; secondaryLabel?: string; icon?: string };

/**
 * Loads a select field's options dynamically from a declared subset of the form values, so a select can
 * depend on a sibling field (e.g. publications for a typed handle). `deps` are the field names the loader
 * reads; the form re-runs it only when one of those values changes. The returned Effect must be
 * self-contained (`R = never`): it provides its own dependencies (network layer, etc.) in the closure.
 * Construct via {@link optionsLookup} for typed `deps`/`values`. Pass `{ combobox: true }` to render
 * an editable combobox (type-to-search) instead of a constrained select.
 */
export type OptionsLookup = {
  readonly deps: readonly string[];
  readonly load: (values: any) => Effect.Effect<readonly OptionsLookupEntry[], unknown>;
  readonly combobox?: boolean;
  /**
   * List every loaded option before anything is typed (a combobox over a small, known catalogue —
   * models, voices), rather than only once the query narrows a remote search.
   */
  readonly eager?: boolean;
};

export const OptionsLookupAnnotationId = '@dxos/schema/annotation/OptionsLookup';
export const OptionsLookupAnnotation = createAnnotationHelper<OptionsLookup>(OptionsLookupAnnotationId);

/**
 * Builds an {@link OptionsLookup} typed against a schema's value type `Values`: `deps` is checked
 * against its field names, and `load` receives only those fields, narrowed. Pass `{ combobox: true }` to
 * render an editable combobox, and `eager: true` to list the loaded options before anything is typed.
 */
export const optionsLookup =
  <Values>() =>
  <const Deps extends readonly (keyof Values & string)[]>(
    deps: Deps,
    load: (values: Pick<Values, Deps[number]>) => Effect.Effect<readonly OptionsLookupEntry[], unknown>,
    options?: { combobox?: boolean; eager?: boolean },
  ): OptionsLookup => ({ deps, load, combobox: options?.combobox, eager: options?.eager });

/**
 * Derives a (text) field's value from a declared subset of the form values, so a field can be pre-filled
 * from a sibling (e.g. a feed name fetched from a typed URL). `deps` are the field names the derivation
 * reads; the form re-runs it only when one of those changes. The returned Effect must be self-contained
 * (`R = never`); `undefined` means "no value to fill". The form pre-fills only while the user has not
 * typed their own value, so a manual edit is never clobbered. Construct via {@link autofill}.
 */
export type Autofill = {
  readonly deps: readonly string[];
  readonly derive: (values: any) => Effect.Effect<string | undefined, unknown>;
};

export const AutofillAnnotationId = '@dxos/schema/annotation/Autofill';
export const AutofillAnnotation = createAnnotationHelper<Autofill>(AutofillAnnotationId);

/** Builds an {@link Autofill} typed against a schema's value type `Values` (see {@link optionsLookup}). */
export const autofill =
  <Values>() =>
  <const Deps extends readonly (keyof Values & string)[]>(
    deps: Deps,
    derive: (values: Pick<Values, Deps[number]>) => Effect.Effect<string | undefined, unknown>,
  ): Autofill => ({ deps, derive });

/** Marks a field whose value is one of the theme's hues; the form renders the hue picker for it. */
export const HueAnnotation = createAnnotationHelper<boolean>(HueAnnotationId);

/**
 * Increment for a number field's stepper buttons and arrow keys. Without it an integer field steps by 1 and
 * any other by 0.1 or 0.01, by the size of its current value.
 */
export const StepAnnotation = createAnnotationHelper<number>(StepAnnotationId);
